import { Component, Input, Output, EventEmitter, ElementRef, Optional } from '@angular/core';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { GtagService } from '../../../Misc/services/gtag.service';
import { PendoService } from '../../../Misc/services/pendo.service';

/**
 * ### Accessibility Usage
 * + ***ariaControls*** use this if clicking the button modify another content. Provide the id of the other content
 * + ***ariaDescribedBy*** - use this if the button has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaHasPopup*** - use this to indicate that this button will open a new popup. We mostly use "dialog" as value, but other values are possible
 * + ***ariaLabel*** - use this to provide an accessible label for the button. Don't use this if tooltip is used and the label is the same as the tooltip
 * + ***expanded*** - use this to indicate controlled area's expand state when the button is a toggle button
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***initiallyFocusable*** - use this if the focusablity of the button can vary and an initial state is needed. Usually used in a table and set to false
 * + ***tabindex*** - use this if the parent wants to directly control the button's focusability. This is not preferred as even when disabled we still
 *                    want the button to be focusable, and we use initiallyFocusable if the button is in a table
 * + ***tooltip*** - use this to display a visual tooltip of the button
 */
@Component({
    selector: 'agpr-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    standalone: false
})
export class ButtonComponent {

    @Input() id: string;
    @Input() style: ButtonTypesEnum = ButtonTypesEnum.secondary;
    @Input() label?: string;
    @Input() tooltip?: string;
    @Input() tooltipPosition?: string = 'right';
    @Input() tooltipStyleClass?: string;
    @Input() icon?: string;
    @Input() imageUrl?: string;
    @Input() imageUrlDefault?: string;
    @Input() imageClass?: string;
    @Input() disabled?: boolean = false;
    @Input() initiallyFocusable: boolean = true;
    @Input() forceTooltipOnFocus: boolean = false;
    @Input() expanded?: boolean;
    @Input() wide?: boolean = false;
    @Input() loading: boolean;
    @Input() loadingIndicatorLightMode: boolean = false;
    @Input() hideImageAlt: boolean = false;
    @Input() ariaLabel?: string;
    @Input() ariaDescribedBy: string;
    @Input() tabindex: string = null;
    @Input() ariaHasPopup: string;
    @Input() ariaControls: string;
    @Input() role?: string;
    @Input() ariaSelected?: boolean;
    @Input() underline: boolean = false;
    @Input() active: boolean = false;
    @Input() tailIcon?: string;

    @Output() click: EventEmitter<PointerEvent> = new EventEmitter<PointerEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() keyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();


    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public element: ElementRef,
        @Optional() private gtagService: GtagService,
        @Optional() private pendoService: PendoService,
    ) {}

    onClick(event: PointerEvent) {
        event.stopImmediatePropagation();
        event.preventDefault();

        if (!this.disabled && !this.loading) {
            this.click.emit(event);
            if (this.gtagService) {
                this.gtagService.buttonPress(this.id).then();
            }
            if (this.pendoService) {
                this.pendoService.trackButtonPress(this.id);
            }
        }
    }

    onKeydown(event: KeyboardEvent) {
        this.keydown.emit(event);
    }

    onKeyup(event: KeyboardEvent) {
        this.keyup.emit(event);
    }
}
